// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DatadogErrorTrackingService } from '@lfx/core/services/datadog-error-tracking.service';

/**
 * Global error handler that catches uncaught JavaScript errors and sends them to Datadog RUM
 *
 * This handler extends Angular's built-in ErrorHandler to:
 * - Capture uncaught JavaScript errors
 * - Capture unhandled promise rejections
 * - Extract component context when available
 * - Send structured error data to Datadog
 *
 * Note: HTTP errors are already handled by DatadogHttpErrorInterceptor,
 * so this handler skips HttpErrorResponse instances.
 *
 * @example
 * Register in the app module:
 * ```typescript
 * providers: [
 *   { provide: ErrorHandler, useClass: DatadogGlobalErrorHandler },
 * ]
 * ```
 */
@Injectable()
export class DatadogGlobalErrorHandler implements ErrorHandler {
  /**
   * Use Injector to get the service lazily to avoid circular dependency issues
   */
  constructor(private injector: Injector) {}

  /**
   * Handle an error thrown in the application
   */
  handleError(error: unknown): void {
    // Always log to console for debugging
    console.error('Uncaught error:', error);

    // Skip HTTP errors - they are handled by the interceptor
    if (error instanceof HttpErrorResponse) {
      return;
    }

    // Get the error tracking service lazily
    const errorTracking = this.injector.get(DatadogErrorTrackingService);

    try {
      // Extract error information
      const errorInfo = this.extractErrorInfo(error);

      // Track the error
      errorTracking.trackJsError(errorInfo.error, {
        feature: 'unknown',
        action: 'runtime-error',
        component: errorInfo.component,
        originalError: errorInfo.originalMessage,
      });
    } catch (trackingError) {
      // If tracking fails, just log it - don't throw
      console.error('Failed to track error to Datadog:', trackingError);
    }
  }

  /**
   * Extract error information from various error types
   */
  private extractErrorInfo(error: unknown): {
    error: Error;
    component?: string;
    originalMessage?: string;
  } {
    // Handle Error instances
    if (error instanceof Error) {
      return {
        error,
        component: this.extractComponentFromStack(error.stack),
        originalMessage: error.message,
      };
    }

    // Handle ErrorEvent (from window.onerror)
    if (error instanceof ErrorEvent) {
      return {
        error: new Error(error.message),
        originalMessage: error.message,
      };
    }

    // Handle promise rejection with reason
    if (this.isPromiseRejection(error)) {
      const reason = (error as { rejection: unknown }).rejection;
      if (reason instanceof Error) {
        return {
          error: reason,
          component: this.extractComponentFromStack(reason.stack),
          originalMessage: reason.message,
        };
      }
      return {
        error: new Error(String(reason)),
        originalMessage: String(reason),
      };
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        error: new Error(error),
        originalMessage: error,
      };
    }

    // Handle unknown error types
    return {
      error: new Error('Unknown error occurred'),
      originalMessage: JSON.stringify(error),
    };
  }

  /**
   * Check if the error is a promise rejection
   */
  private isPromiseRejection(error: unknown): boolean {
    return (
      error !== null &&
      typeof error === 'object' &&
      'rejection' in error
    );
  }

  /**
   * Try to extract component name from error stack trace
   */
  private extractComponentFromStack(stack?: string): string | undefined {
    if (!stack) {
      return undefined;
    }

    // Look for Angular component patterns in stack trace
    // Example: "at KeyProjectsComponent.deleteWorkspace"
    const componentMatch = stack.match(/at\s+(\w+Component)\./);
    if (componentMatch) {
      return componentMatch[1];
    }

    // Look for service patterns
    const serviceMatch = stack.match(/at\s+(\w+Service)\./);
    if (serviceMatch) {
      return serviceMatch[1];
    }

    return undefined;
  }
}

